import {
  AfterViewChecked,
  Component,
  ElementRef,
  Input,
  OnChanges,
  Renderer2,
  RendererStyleFlags2,
  SimpleChanges,
  ViewChild
} from '@angular/core';

import { LazyDataInterface } from 'shared/lazy-load/lazy-load.interface';
import { AssetsSharedService } from 'shared/services/assets-shared.service';


@Component({
  selector: 'render-video',
  templateUrl: './render-video.component.html',
  styleUrls: ['./render-video.component.scss']
})
export class RenderVideoComponent implements OnChanges, AfterViewChecked {

  @Input() public isActive = true;
  @Input() public ariaLabel!: string;
  @Input() public autoPlay = false;
  @Input() public clickable = true;
  @Input() public controls = false;
  @Input() public loop = false;
  @Input() public muted = false;
  @Input() public objectFit!: string;
  @Input() public objectPosition!: string;
  @Input() public poster: LazyDataInterface | undefined;
  @Input() public source!: string;
  @Input() public title!: string;

  @ViewChild('videoElement')
  private readonly ownVideoElement?: ElementRef<HTMLVideoElement>;

  private hasToWaitForHLS = true;

  constructor(
    private readonly assetsSharedService: AssetsSharedService,
    private readonly element: ElementRef,
    private readonly renderer: Renderer2
  ) { }

  public playVideoWithoutControls(video: HTMLVideoElement): void {
    if (!this.controls && this.clickable) {
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!!changes.muted && !changes.muted.isFirstChange() && this.ownVideoElement) {
      this.ownVideoElement.nativeElement.muted = this.muted;
    }

    if (this.objectFit) {
      this.renderer.setStyle(this.element.nativeElement, 'imageObjectFit', `${this.objectFit}`, RendererStyleFlags2.DashCase);
    }

    if (this.objectPosition) {
      this.renderer.setStyle(this.element.nativeElement, 'imageObjectPosition', `${this.objectPosition}`, RendererStyleFlags2.DashCase);
    }
  }

  public async ngAfterViewChecked(): Promise<void> {
    if (!this.isActive) {
      this.hasToWaitForHLS = true;
    } else {
      if (!this.hasToWaitForHLS || !this.ownVideoElement || !this.source) {
        return;
      }

      this.hasToWaitForHLS = false;
      const videoSource = this.ownVideoElement.nativeElement.src;

      if (this.assetsSharedService.isHLSVideo(videoSource)) {
        await this.initHLS(this.ownVideoElement.nativeElement);
      }
    }
  }

  private async initHLS(video: HTMLVideoElement): Promise<void> {
    const Hls = (await import('hls.js')).default;
    if (Hls.isSupported()) {
      const hls = new Hls({
        // Add here any required parameters for advanced tuning.
        // Check https://github.com/video-dev/hls.js/blob/master/docs/API.md
        capLevelToPlayerSize: true
      });
      hls.loadSource(video.src);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (event: string, data: { fatal: boolean; type: string }) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;
            default:
              // Cannot recover
              hls.destroy();
              break;
          }
        }
      });
    } else if (this.ownVideoElement!.nativeElement.canPlayType('application/vnd.apple.mpegurl')) {
      this.ownVideoElement!.nativeElement.src = video.src;
      this.ownVideoElement!.nativeElement.addEventListener('canplay', () => {
        this.ownVideoElement!.nativeElement.play();
      });
    }
  }

}
